/**
 * Persistent cache for GET /api/reservation payloads used by chat reservation
 * cards. Survives remounts and reloads so cancelled badges + checklist status
 * paint instantly. Background revalidation keeps data fresh; mutations
 * (cancel, pet names, etc.) write through immediately.
 */

const STORAGE_KEY = 'doggos_reservation_detail_v1'
const CACHE_VERSION = 2
const MAX_STORED = 40
/** Skip network when the cached payload is newer than this. */
const FRESH_MS = 15 * 60 * 1000

const memoryCache = new Map()
const inflight = new Map()
let storageHydrated = false

function normalizeReservationCodeKey(code) {
  return String(code || '')
    .trim()
    .replace(/^#/, '')
    .toUpperCase()
}

function readAuthHotelId() {
  if (typeof window === 'undefined') return ''
  try {
    const token = window.localStorage.getItem('doggos_l')
    if (!token) return ''
    const base64Url = token.split('.')[1]
    if (!base64Url) return ''
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const profile = JSON.parse(window.atob(padded))
    return String(profile?.user_id || '').trim()
  } catch {
    return ''
  }
}

function readStorage() {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.v !== CACHE_VERSION || !parsed?.byCode) return null
    return parsed
  } catch {
    return null
  }
}

function writeStorage(byCode) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: CACHE_VERSION, byCode }),
    )
  } catch {
    // ignore quota / privacy mode
  }
}

function hydrateFromStorage() {
  if (storageHydrated || typeof window === 'undefined') return
  storageHydrated = true
  const stored = readStorage()
  if (!stored?.byCode) return
  const hotelId = readAuthHotelId()
  Object.entries(stored.byCode).forEach(([key, entry]) => {
    if (!entry?.data) return
    if (hotelId && entry.hotelId && entry.hotelId !== hotelId) return
    if (memoryCache.has(key)) return
    memoryCache.set(key, {
      data: entry.data,
      fetchedAt: Number(entry.fetchedAt) || 0,
      hotelId: entry.hotelId || '',
    })
  })
}

function persistMemoryToStorage() {
  if (typeof window === 'undefined') return
  const hotelId = readAuthHotelId()
  const entries = [...memoryCache.entries()]
    .filter(([, entry]) => entry?.data)
    .filter(([, entry]) => !hotelId || !entry.hotelId || entry.hotelId === hotelId)
    .sort(
      (a, b) =>
        (Number(b[1].fetchedAt) || 0) - (Number(a[1].fetchedAt) || 0),
    )
    .slice(0, MAX_STORED)

  const byCode = {}
  entries.forEach(([key, entry]) => {
    byCode[key] = {
      data: entry.data,
      fetchedAt: entry.fetchedAt,
      hotelId: entry.hotelId || hotelId || '',
    }
  })
  writeStorage(byCode)
}

/**
 * @param {string} code
 * @returns {object|null}
 */
export function getCachedReservationDetail(code) {
  hydrateFromStorage()
  const key = normalizeReservationCodeKey(code)
  if (!key) return null
  const hit = memoryCache.get(key)
  return hit?.data || null
}

/**
 * @param {string} code
 * @returns {{ data: object, fetchedAt: number }|null}
 */
export function getCachedReservationDetailEntry(code) {
  hydrateFromStorage()
  const key = normalizeReservationCodeKey(code)
  if (!key) return null
  return memoryCache.get(key) || null
}

export function isReservationDetailCacheFresh(code, maxAgeMs = FRESH_MS) {
  const entry = getCachedReservationDetailEntry(code)
  if (!entry?.data) return false
  return Date.now() - (Number(entry.fetchedAt) || 0) < maxAgeMs
}

/**
 * @param {string} code
 * @param {object} data
 */
export function setCachedReservationDetail(code, data) {
  const key = normalizeReservationCodeKey(code)
  if (!key || !data) return
  memoryCache.set(key, {
    data,
    fetchedAt: Date.now(),
    hotelId: readAuthHotelId(),
  })
  persistMemoryToStorage()
}

/** Patch fields on a cached reservation without a full refetch. */
export function patchCachedReservationDetail(code, patch) {
  const key = normalizeReservationCodeKey(code)
  if (!key || !patch) return
  hydrateFromStorage()
  const prev = memoryCache.get(key)?.data
  if (!prev) {
    memoryCache.set(key, {
      data: { code: key, ...patch },
      fetchedAt: Date.now(),
      hotelId: readAuthHotelId(),
    })
  } else {
    memoryCache.set(key, {
      data: { ...prev, ...patch, code: key },
      fetchedAt: Date.now(),
      hotelId: readAuthHotelId(),
    })
  }
  persistMemoryToStorage()
}

/** Optimistic cancel stamp used when the hotel cancels from the calendar. */
export function markReservationDetailCancelled(code) {
  patchCachedReservationDetail(code, {
    status: 'cancelled',
    cancelled: true,
    cancelledAt: new Date().toISOString(),
  })
}

/** Optimistic confirm stamp from the chat card attachment (customer Confirm). */
export function markReservationDetailConfirmed(code, confirmedAt) {
  const at =
    typeof confirmedAt === 'string' && confirmedAt.trim()
      ? confirmedAt.trim()
      : new Date().toISOString()
  const prev = getCachedReservationDetail(code)
  patchCachedReservationDetail(code, {
    status: 'confirmed',
    steps: {
      ...(prev?.steps && typeof prev.steps === 'object' ? prev.steps : {}),
      confirmedAt: at,
    },
  })
}

/** Optimistic paid stamp from the chat card attachment (Stripe webhook). */
export function markReservationDetailPaid(code, paidAt, extras = {}) {
  const at =
    typeof paidAt === 'string' && paidAt.trim()
      ? paidAt.trim()
      : new Date().toISOString()
  const prev = getCachedReservationDetail(code)
  const confirmedAt =
    (typeof extras.confirmedAt === 'string' && extras.confirmedAt.trim()) ||
    prev?.steps?.confirmedAt ||
    at
  patchCachedReservationDetail(code, {
    paymentStatus: 'paid',
    ...(extras.balanceStatus ? { balanceStatus: extras.balanceStatus } : {}),
    status:
      extras.status ||
      (prev?.status === 'cancelled' || prev?.status === 'canceled'
        ? prev.status
        : 'confirmed'),
    steps: {
      ...(prev?.steps && typeof prev.steps === 'object' ? prev.steps : {}),
      paidAt: at,
      confirmedAt,
    },
  })
}

export function clearReservationDetailCache() {
  memoryCache.clear()
  inflight.clear()
  storageHydrated = true
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Load reservation detail with localStorage + memory cache.
 * Fresh hits skip the network; otherwise returns cache immediately via
 * getCachedReservationDetail while this promise revalidates.
 *
 * @param {string} code
 * @param {{ force?: boolean, token?: string|null }} [options]
 * @returns {Promise<object>}
 */
export async function fetchReservationDetailCached(code, options = {}) {
  const key = normalizeReservationCodeKey(code)
  if (!key) throw new Error('code_required')

  hydrateFromStorage()
  const force = Boolean(options.force)
  const cached = memoryCache.get(key)
  if (
    !force &&
    cached?.data &&
    Date.now() - (Number(cached.fetchedAt) || 0) < FRESH_MS
  ) {
    return cached.data
  }

  if (inflight.has(key)) {
    return inflight.get(key)
  }

  const run = (async () => {
    const headers = {}
    const token =
      options.token !== undefined
        ? options.token
        : typeof window !== 'undefined'
          ? window.localStorage.getItem('doggos_l')
          : null
    if (token) headers.Authorization = `Bearer ${token}`

    const response = await fetch(
      `/api/reservation?code=${encodeURIComponent(key)}`,
      Object.keys(headers).length ? { headers } : undefined,
    )
    if (!response.ok) {
      throw new Error('Reservation not found')
    }
    const data = await response.json()
    setCachedReservationDetail(key, data)
    return data
  })()

  inflight.set(key, run)
  try {
    return await run
  } finally {
    inflight.delete(key)
  }
}
